import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';
import { LeadForm } from '../components/LeadForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Home as HomeIcon,
  Phone,
  Mail,
  MapPin,
  Star,
  Award,
  TrendingUp,
  Shield,
  ClipboardCheck,
  Scale,
  Search,
  Users,
  CheckCircle2,
  ArrowRight,
  Calendar,
  ChevronRight,
  FileText,
  MessageCircle,
  Menu,
  X,
  Calculator as CalculatorIcon,
  HelpCircle,
  Download,
  Bell,
  Map,
  ChevronDown
} from 'lucide-react';
import { agentInfo, services, testimonials, recentSales } from '../data/mock';
import { trackContactClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'clipboard-check': ClipboardCheck,
  'scale': Scale,
  'home': HomeIcon,
  'search': Search,
  'trending-up': TrendingUp,
  'users': Users,
  'map': Map,
  'bell': Bell,
};

const Home = () => {
  useSeo({
    title: 'Vendetucasa.ar · Portal de Búsqueda y Análisis Inmobiliario en Buenos Aires',
    description: 'Buscador de propiedades con oportunidades, pozo y análisis de mercado en CABA y Zona Norte. Calculadora de valor por barrio con datos reales, actualizados a diario.',
    canonical: 'https://vendetucasa.ar/',
    ogUrl: 'https://vendetucasa.ar/',
  });

  // Lead capture (floating "Contactar" button + hero CTAs)
  const [homeLeadOpen, setHomeLeadOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    size: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [articles, setArticles] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      if (response.data.success) {
        setArticles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, propertyType: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Route the appraisal form through the neutral lead-capture endpoint so
      // all inbound requests land in one inbox.
      const composedMessage = [
        formData.address && `Dirección: ${formData.address}`,
        formData.propertyType && `Tipo: ${formData.propertyType}`,
        formData.size && `Superficie: ${formData.size} m²`,
        formData.reason && `Motivo: ${formData.reason}`,
      ].filter(Boolean).join(' · ');

      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: composedMessage || null,
        lead_type: 'valuacion',
        property_zone: null,
        source_page: '/#appraisal-form',
      });

      toast.success('¡Solicitud enviada!', {
        description: 'Te contactaremos pronto con el análisis del valor de tu propiedad.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        propertyType: '',
        size: '',
        reason: ''
      });
    } catch (error) {
      console.error('Error submitting appraisal request:', error);
      toast.error('Error al enviar', {
        description: error.response?.data?.detail || 'Por favor intenta nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('appraisal-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="relative">
              <svg viewBox="0 0 100 100" className="w-10 h-10">
                <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                <circle cx="25" cy="25" r="5" fill="white"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">
              <span className="text-[#E31C23]">vende</span>
              <span className="text-slate-600">tu</span>
              <span className="text-[#E31C23]">casa</span>
              <span className="text-[#003DA5]">.ar</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-5">
            <a href="#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</a>
            <a href="#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</a>
            <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Calculadora</Link>
            <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Barrios</Link>
            <Link to="/propiedades" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Propiedades</Link>
            <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Noticias</Link>
            <a href="#faq" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">FAQ</a>
            <a href="#contact" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Contacto</a>
          </nav>
          
          <div className="flex items-center gap-3">
            <Link 
              to="/calculadora" 
              className="calculator-pulse hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#003DA5] text-white hover:bg-[#E31C23] hover:scale-110 transition-all duration-200 shadow-md"
              title="Calculadora de Precios"
            >
              <CalculatorIcon className="w-5 h-5" />
            </Link>
            <Button onClick={scrollToForm} className="hidden sm:flex bg-[#E31C23] hover:bg-[#C11A1F] text-white">
              Conocé el Valor de tu Casa
            </Button>
            
            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <a 
                href="#about" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sobre Mí
              </a>
              <a 
                href="#services" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Servicios
              </a>
              <Link 
                to="/calculadora" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2 flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Calculadora
              </Link>
              <Link 
                to="/barrios" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2 flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Guía de Barrios
              </Link>
              <Link 
                to="/propiedades" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2 flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="bg-[#E31C23] text-white text-xs px-2 py-0.5 rounded mr-2">Nuevo</span>
                Propiedades
              </Link>
              <Link 
                to="/noticias" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Noticias
              </Link>
              <a 
                href="#faq" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </a>
              <a 
                href="#contact" 
                className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contacto
              </a>
              <Button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToForm();
                }} 
                className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2"
              >
                Conocé el Valor de tu Casa
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1627141234469-24711efb373c?w=1920&q=80)',
          }}
        ></div>
        {/* Blue Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#003DA5]/90 via-[#003DA5]/85 to-[#0051D5]/90"></div>
        
        {/* Balloon decoration */}
        <div className="absolute top-10 right-10 opacity-15 hidden lg:block">
          <svg width="160" height="160" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="50" cy="45" rx="35" ry="40" fill="white"/>
            <rect x="48" y="75" width="4" height="20" fill="white"/>
            <ellipse cx="50" cy="30" rx="20" ry="22" fill="white" opacity="0.3"/>
          </svg>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo Principal */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <svg viewBox="0 0 100 100" className="w-20 h-20 md:w-28 md:h-28">
                    <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                    <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                    <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                    <circle cx="25" cy="25" r="5" fill="white"/>
                  </svg>
                </div>
                <span className="text-4xl md:text-6xl font-bold">
                  <span className="text-white">vende</span>
                  <span className="text-blue-200">tu</span>
                  <span className="text-white">casa</span>
                  <span className="text-blue-300">.ar</span>
                </span>
              </div>
            </div>

            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
              <Award className="w-4 h-4 text-[#E31C23] mr-2" />
              <span className="text-sm font-medium text-white">Portal Inmobiliario Independiente</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg leading-tight">
              Encontrá <span className="text-[#E31C23]">oportunidades</span> inmobiliarias en Buenos Aires
            </h1>
            <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
              Buscador con datos reales de mercado, filtro de propiedades en pozo y análisis de valor por barrio.
            </p>
            <p className="text-base text-white mb-8 max-w-2xl mx-auto opacity-90">
              Sin agentes personales · Datos actualizados a diario · Cobertura CABA + Zona Norte
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-[#E31C23] hover:bg-[#C11A1F] text-white shadow-lg hover:shadow-xl transition-all">
                <Link to="/propiedades">
                  Ver Propiedades
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003DA5] transition-all">
                <Link to="/calculadora">Calcular Valor</Link>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-12">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">67</div>
                <div className="text-sm text-white opacity-90">Barrios Cubiertos</div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">+2.400</div>
                <div className="text-sm text-white opacity-90">Propiedades Activas</div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-4">
                <div className="text-3xl font-bold text-white">24hs</div>
                <div className="text-sm text-white opacity-90">Datos Actualizados</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Sobre Vendetucasa.ar</h2>
              <div className="w-20 h-1 bg-[#E31C23] mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 h-80 flex items-center justify-center overflow-hidden">
                <svg viewBox="0 0 100 100" className="w-48 h-48">
                  <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#003DA5"/>
                  <circle cx="50" cy="50" r="12" fill="#E31C23"/>
                  <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#E31C23"/>
                  <circle cx="25" cy="25" r="5" fill="white"/>
                </svg>
              </div>
              <div>
                <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                  Somos un portal inmobiliario <strong>independiente</strong> enfocado en darle a los compradores e inversores herramientas que ninguna otra plataforma ofrece: detección de oportunidades bajo precio de mercado, filtro específico de propiedades en pozo con año de entrega, calculadora de valor por barrio con datos reales y guía comparativa de 67 barrios.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Shield className="w-6 h-6 text-[#E31C23] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#003DA5]">Sin agentes intermediarios</h3>
                      <p className="text-slate-600">Cada listado te lleva a la publicación original con su contacto verificado.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <TrendingUp className="w-6 h-6 text-[#E31C23] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#003DA5]">Datos reales, no estimaciones</h3>
                      <p className="text-slate-600">Precios promedio calculados sobre miles de listings activos, actualizados a diario.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-[#E31C23] mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#003DA5]">Herramientas gratuitas</h3>
                      <p className="text-slate-600">Buscador, calculadora, guía de barrios, mapa y filtros avanzados sin registro obligatorio.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Guide CTA */}
      <section className="py-16 bg-gradient-to-r from-[#003DA5] to-[#0051D5]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Map className="w-16 h-16 mx-auto mb-6 text-blue-200" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Guía de Barrios</h2>
            <p className="text-xl text-blue-100 mb-8">
              Explorá los 48 barrios de CABA y Zona Norte. Compará precios, seguridad, 
              transporte y más para encontrar tu lugar ideal.
            </p>
            <Button asChild size="lg" className="bg-white text-[#003DA5] hover:bg-blue-50">
              <Link to="/barrios">
                <Map className="w-5 h-5 mr-2" />
                Explorar Barrios
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Servicios</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Soluciones integrales para todas tus necesidades inmobiliarias
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-blue-100 hover:shadow-lg transition-shadow hover:border-[#E31C23]">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#E31C23] rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl text-[#003DA5]">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Articles/News Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Noticias y Artículos</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Mantente informado sobre el mercado inmobiliario
            </p>
          </div>
          {articles.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">Próximamente encontrarás artículos de interés aquí</p>
              <p className="text-slate-500 text-sm">Estamos preparando contenido valioso para ti</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {articles.slice(0, 3).map((article) => (
                  <Card key={article.id} className="border-slate-200 hover:shadow-lg transition-shadow overflow-hidden group">
                    {article.image_url && (
                      <Link to={`/noticias/${article.slug || article.id}`} className="block h-48 overflow-hidden cursor-pointer">
                        <img 
                          src={article.image_url} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                    )}
                    <CardHeader>
                      <div className="flex items-center text-xs text-slate-500 mb-2">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(article.created_at).toLocaleDateString('es-AR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </div>
                      <Link to={`/noticias/${article.slug || article.id}`}>
                        <CardTitle className="text-xl text-[#003DA5] line-clamp-2 group-hover:text-[#E31C23] transition-colors cursor-pointer">
                          {article.title}
                        </CardTitle>
                      </Link>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="text-slate-600 line-clamp-3 mb-4 prose prose-sm"
                        dangerouslySetInnerHTML={{ 
                          __html: article.excerpt || article.content.substring(0, 150) + '...' 
                        }}
                      />
                      <Button 
                        asChild
                        variant="link" 
                        className="text-[#E31C23] hover:text-[#C11A1F] p-0"
                      >
                        <Link to={`/noticias/${article.slug || article.id}`}>
                          Leer más <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {articles.length > 3 && (
                <div className="text-center mt-8">
                  <Button asChild variant="outline" className="border-[#003DA5] text-[#003DA5] hover:bg-[#003DA5] hover:text-white">
                    <Link to="/noticias">
                      Ver todas las noticias <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Appraisal Form Section */}
      <section id="appraisal-form" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">
                Análisis Comparativo de Mercado
              </h2>
              <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
              <p className="text-lg text-slate-600">
                Completá el formulario y te contacto en menos de 24 horas con una evaluación profesional
              </p>
            </div>
            
            <Card className="border-blue-100 shadow-lg">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#003DA5]">Nombre Completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Juan Pérez"
                        className="mt-1 border-blue-200 focus:border-[#E31C23]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#003DA5]">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="juan@ejemplo.com"
                        className="mt-1 border-blue-200 focus:border-[#E31C23]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#003DA5]">Teléfono *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+54 11 XXXX-XXXX"
                        className="mt-1 border-blue-200 focus:border-[#E31C23]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="propertyType" className="text-[#003DA5]">Tipo de Propiedad *</Label>
                      <Select value={formData.propertyType} onValueChange={handleSelectChange} required>
                        <SelectTrigger className="mt-1 border-blue-200">
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="casa">Casa</SelectItem>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="terreno">Terreno</SelectItem>
                          <SelectItem value="local">Local Comercial</SelectItem>
                          <SelectItem value="oficina">Oficina</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-[#003DA5]">Dirección de la Propiedad *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Calle, número, barrio, ciudad"
                      className="mt-1 border-blue-200 focus:border-[#E31C23]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="size" className="text-[#003DA5]">Superficie Aproximada (m²)</Label>
                    <Input
                      id="size"
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      placeholder="Ej: 85"
                      className="mt-1 border-blue-200 focus:border-[#E31C23]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reason" className="text-[#003DA5]">Motivo de la Consulta</Label>
                    <Textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Quiero vender, comprar, conocer el valor de mercado, etc."
                      className="mt-1 border-blue-200 focus:border-[#E31C23]"
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#E31C23] hover:bg-[#C11A1F] text-white text-lg py-6"
                  >
                    {isSubmitting ? 'Enviando...' : 'Solicitar Análisis de Mercado'}
                  </Button>

                  <p className="text-sm text-slate-500 text-center">
                    Al enviar este formulario, aceptás que te contactemos para brindarte información sobre tu propiedad.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Testimonios</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
            <p className="text-lg text-slate-600">
              Lo que dicen mis clientes
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-blue-100 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#E31C23] text-[#E31C23]" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-[#003DA5]">{testimonial.name}</p>
                    <p className="text-sm text-slate-600">{testimonial.property}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Sales Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Ventas Recientes</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {recentSales.map((sale) => (
              <Card key={sale.id} className="border-blue-100 hover:shadow-lg transition-shadow overflow-hidden">
                <CardContent className="p-0">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={sale.image} 
                      alt={`${sale.type} en ${sale.location}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-lg text-[#003DA5]">{sale.type}</h3>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">VENDIDO</Badge>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2 text-[#E31C23]" />
                        {sale.location}
                      </p>
                      <p className="flex items-center">
                        <HomeIcon className="w-4 h-4 mr-2 text-slate-400" />
                        {sale.size}
                      </p>
                      <p className="text-xl font-bold text-[#E31C23] mt-3">{sale.price}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Contacto</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-8"></div>
            <p className="text-lg text-slate-600 mb-8">
              ¿Necesitás ayuda con tu búsqueda o querés recibir alertas de oportunidades? Dejanos tu consulta.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-[#E31C23] rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#003DA5] mb-2">Email</h3>
                <p className="text-slate-600">contacto@vendetucasa.ar</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-[#E31C23] rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#003DA5] mb-2">Formulario</h3>
                <Button
                  onClick={() => setHomeLeadOpen(true)}
                  className="bg-[#E31C23] hover:bg-red-700 text-white"
                  data-testid="home-contact-open-lead"
                >
                  Dejar consulta
                </Button>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-[#E31C23] rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#003DA5] mb-2">Cobertura</h3>
                <p className="text-slate-600 text-center">CABA + Zona Norte</p>
                <p className="text-slate-600 text-center">67 barrios</p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003DA5] mb-4">Preguntas Frecuentes</h2>
            <div className="w-20 h-1 bg-[#E31C23] mx-auto mb-4"></div>
            <p className="text-lg text-slate-600">
              Todo lo que necesitás saber sobre comprar o vender tu propiedad
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: "¿Cómo puedo conocer el valor de mi propiedad?",
                answer: "Te ofrezco un análisis comparativo de mercado sin compromiso. Evalúo tu propiedad basándome en datos actuales, propiedades similares vendidas recientemente y mi experiencia en la zona para darte un valor de referencia preciso."
              },
              {
                question: "¿Qué impuestos se pagan al vender una propiedad?",
                answer: "Buenas noticias: el ITI (Impuesto a la Transferencia de Inmuebles) fue eliminado en julio 2024. Para propiedades adquiridas después de 2018, aplica un impuesto cedular del 15% sobre la ganancia (precio de venta menos costo actualizado). Actualmente se está debatiendo la eliminación de este último impuesto."
              },
              {
                question: "¿Cuánto tiempo tarda vender una propiedad?",
                answer: "Depende de varios factores: precio, ubicación, estado y demanda del mercado. En promedio, una propiedad bien tasada se vende entre 3 y 6 meses. Con la estrategia correcta, muchas se venden en menos de 90 días."
              },
              {
                question: "¿Qué documentación necesito para vender?",
                answer: "Los documentos esenciales son: título de propiedad, planos aprobados, reglamento de copropiedad (en edificios), libre deuda de expensas, impuestos municipales y ABL al día, y facturas de servicios. Te ayudo a gestionar todo."
              },
              {
                question: "¿Cuál es la comisión por venta?",
                answer: "La comisión estándar de mercado es del 3% + IVA para el vendedor y 4% + IVA para el comprador. Esta comisión incluye análisis de mercado, fotografía profesional, publicación en portales, gestión de visitas y asesoramiento durante todo el proceso."
              },
              {
                question: "¿Cómo funcionan los contratos de alquiler actualmente?",
                answer: "Desde el DNU 70/2023, los contratos de alquiler son libres: podés pactar cualquier duración, moneda (incluso dólares) y frecuencia de actualización. Ya no existe el plazo mínimo de 3 años ni el índice de actualización obligatorio."
              },
              {
                question: "¿Conviene vender ahora o esperar?",
                answer: "El mercado inmobiliario argentino está en recuperación. Con créditos hipotecarios más accesibles (Decreto 1017/2024) y el fin del cepo, la demanda está creciendo. Sin embargo, cada caso es único. Te asesoro según tu situación particular."
              }
            ].map((faq, index) => (
              <details key={index} className="group bg-slate-50 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <div className="flex items-center">
                    <HelpCircle className="w-5 h-5 text-[#E31C23] mr-3 flex-shrink-0" />
                    <span className="font-semibold text-[#003DA5] text-left">{faq.question}</span>
                  </div>
                  <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-slate-700 pl-8">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist Download & Property Alerts Section */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Checklist Download */}
            <Card className="border-blue-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-[#003DA5] rounded-full flex items-center justify-center mb-4">
                  <Download className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#003DA5]">Checklist: 10 Pasos para Vender</CardTitle>
                <CardDescription>
                  Guía gratuita con todo lo que necesitás saber para vender tu propiedad con éxito
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const email = e.target.checklistEmail.value;
                  const name = e.target.checklistName.value;
                  try {
                    const response = await axios.post(`${API}/subscriptions/checklist`, { email, name });
                    if (response.data.success) {
                      toast.success('¡Checklist enviado! Revisá los pasos a continuación.');
                      // Could show modal with checklist content
                    }
                  } catch (error) {
                    toast.error('Error al procesar la solicitud');
                  }
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="checklistName">Nombre</Label>
                    <Input id="checklistName" name="checklistName" placeholder="Tu nombre" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="checklistEmail">Email *</Label>
                    <Input id="checklistEmail" name="checklistEmail" type="email" placeholder="tu@email.com" required className="mt-1" />
                  </div>
                  <Button type="submit" className="w-full bg-[#003DA5] hover:bg-[#002d7a]">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Checklist Gratis
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Property Alerts */}
            <Card className="border-red-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-[#E31C23] rounded-full flex items-center justify-center mb-4">
                  <Bell className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-[#003DA5]">Alertas de Propiedades</CardTitle>
                <CardDescription>
                  Recibí notificaciones cuando haya propiedades que coincidan con tu búsqueda
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  try {
                    const response = await axios.post(`${API}/subscriptions/alerts`, {
                      email: formData.get('alertEmail'),
                      name: formData.get('alertName'),
                      zones: [formData.get('alertZone')],
                      interest: formData.get('alertInterest'),
                      property_types: [formData.get('alertPropertyType')],
                      budget_min: formData.get('alertBudgetMin') ? parseFloat(formData.get('alertBudgetMin')) : null,
                      budget_max: formData.get('alertBudgetMax') ? parseFloat(formData.get('alertBudgetMax')) : null,
                      rooms_min: formData.get('alertRoomsMin') ? parseInt(formData.get('alertRoomsMin')) : null,
                      rooms_max: formData.get('alertRoomsMax') ? parseInt(formData.get('alertRoomsMax')) : null,
                      size_min: formData.get('alertSizeMin') ? parseInt(formData.get('alertSizeMin')) : null,
                      size_max: formData.get('alertSizeMax') ? parseInt(formData.get('alertSizeMax')) : null
                    });
                    if (response.data.success) {
                      toast.success('¡Suscripción exitosa! Te avisaremos cuando haya novedades.');
                      e.target.reset();
                    }
                  } catch (error) {
                    toast.error('Error al procesar la suscripción');
                  }
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="alertName">Nombre</Label>
                      <Input id="alertName" name="alertName" placeholder="Tu nombre" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="alertEmail">Email *</Label>
                      <Input id="alertEmail" name="alertEmail" type="email" placeholder="tu@email.com" required className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="alertZone">Zona de interés *</Label>
                      <Select name="alertZone" defaultValue="CABA">
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Elegir zona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CABA">CABA</SelectItem>
                          <SelectItem value="Zona Norte">Zona Norte</SelectItem>
                          <SelectItem value="Palermo">Palermo</SelectItem>
                          <SelectItem value="Belgrano">Belgrano</SelectItem>
                          <SelectItem value="Recoleta">Recoleta</SelectItem>
                          <SelectItem value="Caballito">Caballito</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="alertInterest">Interés *</Label>
                      <Select name="alertInterest" defaultValue="compra">
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compra">Comprar</SelectItem>
                          <SelectItem value="alquiler">Alquilar</SelectItem>
                          <SelectItem value="inversion">Invertir</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="alertPropertyType">Tipo de propiedad</Label>
                      <Select name="alertPropertyType" defaultValue="departamento">
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="casa">Casa</SelectItem>
                          <SelectItem value="ph">PH</SelectItem>
                          <SelectItem value="local">Local</SelectItem>
                          <SelectItem value="oficina">Oficina</SelectItem>
                          <SelectItem value="terreno">Terreno</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="alertRooms">Ambientes</Label>
                      <div className="flex gap-2 mt-1">
                        <Input id="alertRoomsMin" name="alertRoomsMin" type="number" placeholder="Mín" min="1" max="10" className="w-1/2" />
                        <Input id="alertRoomsMax" name="alertRoomsMax" type="number" placeholder="Máx" min="1" max="10" className="w-1/2" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Presupuesto (USD)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input name="alertBudgetMin" type="number" placeholder="Mín" min="0" className="w-1/2" />
                        <Input name="alertBudgetMax" type="number" placeholder="Máx" min="0" className="w-1/2" />
                      </div>
                    </div>
                    <div>
                      <Label>Superficie (m²)</Label>
                      <div className="flex gap-2 mt-1">
                        <Input name="alertSizeMin" type="number" placeholder="Mín" min="0" className="w-1/2" />
                        <Input name="alertSizeMax" type="number" placeholder="Máx" min="0" className="w-1/2" />
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-[#E31C23] hover:bg-[#C11A1F]">
                    <Bell className="w-4 h-4 mr-2" />
                    Activar Alertas
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <svg viewBox="0 0 100 100" className="w-16 h-16">
                    <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                    <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                    <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                    <circle cx="25" cy="25" r="5" fill="white"/>
                  </svg>
                </div>
                <span className="text-4xl font-bold">
                  <span className="text-white">vende</span>
                  <span className="text-blue-200">tu</span>
                  <span className="text-white">casa</span>
                  <span className="text-blue-300">.ar</span>
                </span>
              </div>
            </div>
            <p className="text-blue-100 mb-2">Portal Inmobiliario · CABA + Zona Norte</p>
            <p className="text-blue-200 text-sm">
              © {new Date().getFullYear()} Vendetucasa.ar - Todos los derechos reservados
            </p>
          </div>
        </div>
      </footer>

      {/* Floating "Contactar" Button — opens the lead capture form */}
      <button
        type="button"
        onClick={() => { trackContactClick('lead_form', '/inicio'); setHomeLeadOpen(true); }}
        className="fixed bottom-6 right-6 z-50 bg-[#E31C23] hover:bg-red-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        aria-label="Contactanos"
        data-testid="home-floating-contact"
      >
        <MessageCircle className="w-7 h-7" />
        <span className="absolute right-full mr-3 bg-white text-slate-700 px-3 py-2 rounded-lg shadow-md text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          ¿Necesitás ayuda?
        </span>
      </button>

      {/* Home-level lead form */}
      <LeadForm
        open={homeLeadOpen}
        onClose={() => setHomeLeadOpen(false)}
        defaultLeadType="info"
        title="Consultanos"
        sourcePage="/"
      />
    </div>
  );
};

export default Home;